import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { STORE_ADDRESS, STORE_PHONE, HEATHER_IMG, HEATHER_NAME } from "@/lib/brand";
import { Mail, Phone, MapPin, Clock, Instagram, Facebook, Music2, CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { mpLead } from "@/lib/metaPixel";

/**
 * Contact — a warm, human page. Includes a general-inquiry form (routes to
 * info@heathersboutique.com), boutique details, hours, map, and social. The
 * form here is intentionally separate from /support (which is scoped to
 * bugs/orders and routes to support@) — this one is for visit questions,
 * private appointments, press, custom orders, and general notes.
 */

const TOPIC_OPTIONS = [
  { value: "general", label: "General Inquiry" },
  { value: "visit", label: "Visit / Fitting Question" },
  { value: "private-appointment", label: "Private Appointment" },
  { value: "custom-order", label: "Custom Order / Alterations" },
  { value: "press", label: "Press & Media" },
  { value: "wholesale", label: "Wholesale / Retailer" },
];

const inputCls =
  "w-full bg-ivory border border-cream rounded-sm px-3.5 py-3 font-body text-[15px] text-ink placeholder:text-ink/40 outline-none focus:border-rosemetal transition-colors";

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [inquiryId, setInquiryId] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "general",
    message: "",
    website: "", // honeypot
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      toast.error("Please fill in your name, email, and a message of at least 10 characters.");
      return;
    }
    setStatus("loading");
    try {
      const { data } = await api.contactInquiry(form);
      setInquiryId(data.inquiry_id);
      setStatus("success");
      try {
        window.posthog?.capture("contact_inquiry_submit", { topic: form.topic });
      } catch { /* noop */ }
      try { mpLead({ source: "contact_form", topic: form.topic }); } catch { /* noop */ }
    } catch (err) {
      setStatus("idle");
      toast.error(errMsg(err));
    }
  };

  const reset = () => {
    setStatus("idle");
    setInquiryId("");
    setForm({ name: "", email: "", phone: "", topic: "general", message: "", website: "" });
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Visit, call, or send a note to Heather's Boutique in Oconomowoc, Wisconsin. Real humans, real fittings, no chatbot loops."
        path="/contact"
      />

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-16 pb-24">
        <motion.header
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-3">HEATHER'S BOUTIQUE · CONTACT</p>
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-ink">Come see us. Or drop a note.</h1>
          <p className="font-body text-base md:text-lg text-ink/70 mt-4 max-w-2xl leading-relaxed">
            We're a small, family-owned boutique in Oconomowoc. When you call, you get a real person — usually
            Heather or one of her stylists. When you visit, we've probably already saved your name.
          </p>
        </motion.header>

        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          {/* --------------- LEFT: FORM --------------- */}
          <div>
            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contact-success"
                className="border border-cream bg-cream/40 rounded-sm p-8"
              >
                <span className="inline-flex w-14 h-14 rounded-full bg-rosemetal/10 items-center justify-center mb-5">
                  <CheckCircle2 className="w-7 h-7 text-rosemetal" strokeWidth={1.5} />
                </span>
                <p className="font-label text-[10px] tracking-widest text-rosemetal mb-2">MESSAGE RECEIVED</p>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">Thanks — we'll be in touch.</h2>
                <p className="font-body text-[15px] text-ink/70 mt-4 leading-relaxed">
                  A real human on the Heather's team will reply to <b>{form.email}</b> within one business day. If it's
                  urgent, call or text the store at <a href={`tel:${STORE_PHONE.replace(/\D/g, "")}`} className="text-rosemetal hover:text-ink">{STORE_PHONE}</a>.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <span className="font-label text-[10px] tracking-widest text-ink/50">INQUIRY ID</span>
                  <span data-testid="contact-inquiry-id" className="font-mono text-sm px-3 py-1.5 bg-ink text-cream rounded-sm">{inquiryId}</span>
                </div>
                <button
                  onClick={reset}
                  data-testid="contact-new-message"
                  className="mt-8 inline-flex items-center gap-2 font-label text-[10px] tracking-widest text-rosemetal hover:text-ink"
                >
                  Send another note →
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <p className="font-label text-[10px] tracking-[0.28em] text-ink/50 mb-1">SEND US A NOTE</p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">YOUR NAME</label>
                    <input
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Riley Thompson"
                      data-testid="contact-name"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">EMAIL</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      data-testid="contact-email"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">PHONE <span className="text-ink/40">· OPTIONAL</span></label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="(555) 555-0123"
                      data-testid="contact-phone"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">TOPIC</label>
                    <select
                      value={form.topic}
                      onChange={set("topic")}
                      data-testid="contact-topic"
                      className={inputCls}
                    >
                      {TOPIC_OPTIONS.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">YOUR MESSAGE</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={set("message")}
                    minLength={10}
                    rows={6}
                    placeholder="Tell us what you're looking for — a visit, a fitting, a private appointment, or just a question."
                    data-testid="contact-message"
                    className={`${inputCls} resize-y min-h-[140px]`}
                  />
                  <p className="font-label text-[9px] tracking-widest text-ink/40 mt-2">MIN 10 CHARACTERS</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set("website")}
                  aria-hidden="true"
                  className="hidden"
                />

                <div className="flex flex-wrap items-center gap-4 pt-1">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 font-label text-[10px] tracking-[0.3em] rounded-sm hover:bg-rosemetal transition-colors disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> SENDING…</>
                    ) : (
                      <>SEND NOTE →</>
                    )}
                  </button>
                  <p className="font-label text-[9px] tracking-widest text-ink/40">
                    ROUTES TO INFO@HEATHERSBOUTIQUE.COM
                  </p>
                </div>

                <p className="font-body text-xs text-ink/50 pt-3 border-t border-cream">
                  Have an order or website issue? Please use our <Link to="/support" className="text-rosemetal hover:text-ink underline underline-offset-2">Support page</Link> instead — those go to a different team.
                </p>
              </form>
            )}
          </div>

          {/* --------------- RIGHT: BOUTIQUE INFO + MAP --------------- */}
          <aside className="space-y-6">
            <div className="bg-cream/60 border border-cream rounded-sm p-6 flex items-start gap-4">
              <img src={HEATHER_IMG} alt={HEATHER_NAME} className="w-16 h-16 rounded-full object-cover object-top border-2 border-cream shadow-md shrink-0" />
              <div>
                <p className="font-label text-[9px] tracking-widest text-rosemetal">A NOTE FROM HEATHER</p>
                <p className="font-display italic text-ink text-lg leading-snug mt-1">
                  "The fastest way to reach me is a text. I answer between fittings — usually within a few minutes during
                  business hours."
                </p>
              </div>
            </div>

            <ul className="space-y-4 font-body text-[15px] text-ink/80">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-rosemetal mt-1" strokeWidth={1.75} />
                <div>
                  <a href={`tel:${STORE_PHONE.replace(/\D/g, "")}`} className="font-display text-2xl text-ink hover:text-rosemetal">{STORE_PHONE}</a>
                  <p className="font-label text-[10px] tracking-widest text-ink/50 mt-1">CALL OR TEXT · HEATHER OR A STYLIST</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-rosemetal mt-1" strokeWidth={1.75} />
                <div className="space-y-1.5">
                  <div><a href="mailto:info@heathersboutique.com" className="hover:text-rosemetal">info@heathersboutique.com</a> <span className="text-ink/40 text-xs">— general questions</span></div>
                  <div><a href="mailto:support@heathersboutique.com" className="hover:text-rosemetal">support@heathersboutique.com</a> <span className="text-ink/40 text-xs">— orders, returns & help</span></div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-rosemetal mt-1" strokeWidth={1.75} />
                <div>
                  <div>{STORE_ADDRESS}</div>
                  <a href="https://www.google.com/maps/dir/?api=1&destination=W359N5002+Brown+Street+Oconomowoc+WI+53066" target="_blank" rel="noopener noreferrer" className="font-label text-[10px] tracking-widest text-rosemetal hover:text-ink mt-1 inline-block">GET DIRECTIONS →</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-rosemetal mt-1" strokeWidth={1.75} />
                <div className="text-sm">
                  <div>Monday–Saturday · 11am — 4:30pm</div>
                  <div>Sunday · Closed</div>
                </div>
              </li>
            </ul>

            {/* Social */}
            <div className="pt-4 border-t border-cream flex items-center gap-3 flex-wrap">
              <span className="font-label text-[10px] tracking-widest text-ink/60">FOLLOW ALONG</span>
              <a href="https://instagram.com/_heathersboutique" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full border border-cream flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors"><Instagram className="w-4 h-4" strokeWidth={1.5} /></a>
              <a href="https://tiktok.com/@heathersboutique" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-9 h-9 rounded-full border border-cream flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors"><Music2 className="w-4 h-4" strokeWidth={1.5} /></a>
              <a href="https://facebook.com/heatherssboutique" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full border border-cream flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors"><Facebook className="w-4 h-4" strokeWidth={1.5} /></a>
            </div>

            {/* Google Maps iframe */}
            <div className="rounded-sm overflow-hidden border border-cream min-h-[280px] bg-cream/40">
              <iframe
                title="Heather's Boutique map"
                src="https://www.google.com/maps?q=W359N5002%20Brown%20Street%20Suite%20211%20Oconomowoc%20WI%2053066&output=embed"
                className="w-full h-full min-h-[280px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </aside>
        </div>

        {/* Quick links */}
        <div className="mt-16 pt-10 border-t border-cream">
          <p className="font-label text-[10px] tracking-widest text-rosemetal mb-4">MIGHT ALSO HELP</p>
          <div className="flex flex-wrap gap-3">
            {[
              { to: "/support", label: "Support & help" },
              { to: "/returns", label: "Returns policy" },
              { to: "/shipping", label: "Shipping policy" },
              { to: "/parents", label: "For parents" },
              { to: "/try-on", label: "Try on a dress" },
              { to: "/prom-cup", label: "Prom Cup rules" },
            ].map((q) => (
              <Link key={q.to} to={q.to} className="border border-cream rounded-full px-4 py-2 font-body text-sm text-ink hover:border-rosemetal hover:text-rosemetal transition-colors">
                {q.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
